// Q12 grouped Custom list — pure grouping over the fetched rows, no extra
// API calls. Groups by entity, clusters competing rules in firing order.
// TRANSITION: blocks compete per from→to pair, auto-moves per from-status
// (the engine matches AUTO_TRANSITION on from_state only).

import { UpdateWorkflowInput, Workflow } from "src/network/graphql/workflowService";
import {
  AllocationDefaultsScopeShape,
  ENTITY_TYPE_ACCOUNTING,
  ENTITY_TYPE_ALLOCATION,
  ENTITY_TYPE_CARD,
  ENTITY_TYPE_CARD_SETTING,
  ENTITY_TYPE_REIMBURSEMENT,
  ENTITY_TYPE_TRANSACTION,
  ENTITY_TYPE_WALLET,
  SUBSYSTEM_DEFAULTS,
  SUBSYSTEM_NOTIFICATION,
  SUBSYSTEM_OPTIONS,
  SUBSYSTEM_SCHEDULER,
  isBlockTransitionAction,
  isDefaultsSubsystem,
  isTransitionSubsystem,
  subsystemFallbackCopy,
  subsystemPlainLabel,
  workflowStatusLabel
} from "./workflowSubsystems";
import { parseJson } from "./workflowUtils";

export interface WorkflowCluster {
  key: string;
  subsystem: string;
  entity_type: string;
  action?: string;
  from_state?: string;
  to_state?: string;
  rows: Workflow[];
}

export interface WorkflowEntityGroup {
  entity_type: string;
  clusters: WorkflowCluster[];
}

export type WorkflowListRow =
  | { kind: "group"; id: string; entity_type: string }
  | { kind: "cluster"; id: string; cluster: WorkflowCluster }
  | { kind: "fallback"; id: string; subsystem: string }
  | { kind: "workflow"; id: string; workflow: Workflow };

interface WorkflowObjectTab {
  value: string;
  label: string;
}

export const WORKFLOW_OBJECT_TABS: WorkflowObjectTab[] = [
  { value: ENTITY_TYPE_ACCOUNTING, label: "Transaction records" },
  { value: ENTITY_TYPE_REIMBURSEMENT, label: "Claims" },
  { value: ENTITY_TYPE_TRANSACTION, label: "Card payments" },
  { value: ENTITY_TYPE_WALLET, label: "Wallets" },
  { value: ENTITY_TYPE_CARD, label: "Cards" },
  { value: ENTITY_TYPE_CARD_SETTING, label: "Card connections" },
  { value: ENTITY_TYPE_ALLOCATION, label: "Allocation" }
];

export const DEFAULT_WORKFLOW_OBJECT_TAB = ENTITY_TYPE_ACCOUNTING;

export const resolveWorkflowObjectTab = (value?: string | null): string =>
  WORKFLOW_OBJECT_TABS.some((tab) => tab.value === value) ? String(value) : DEFAULT_WORKFLOW_OBJECT_TAB;

// search_workflow caps size at 100 server-side; a list tab loads one page.
export const WORKFLOW_SEARCH_PAGE_SIZE = 100;

// Engine default when a DEFAULTS scope omits priority.
const DEFAULTS_SCOPE_PRIORITY = 1000;

const ORDER_PRIORITY_STEP = 100;

// DEFAULTS rules fire by scope.priority; every other subsystem by top-level priority.
const firingPriority = (workflow: Workflow): number => {
  if (!isDefaultsSubsystem(workflow.subsystem)) return workflow.priority;
  const scope = parseJson<Partial<AllocationDefaultsScopeShape>>(workflow.scope);

  if (!scope) return workflow.priority;
  return typeof scope.priority === "number" ? scope.priority : DEFAULTS_SCOPE_PRIORITY;
};

// Firing order — priority asc, ties broken by creation.
const byFiringOrder = (a: Workflow, b: Workflow): number => {
  const diff = firingPriority(a) - firingPriority(b);

  if (diff !== 0) return diff;
  return (a.created_at ?? "").localeCompare(b.created_at ?? "");
};

const isAutoTransition = (subsystem?: string | null, action?: string | null): boolean =>
  isTransitionSubsystem(subsystem) && !isBlockTransitionAction(action);

// The fields that decide which group a rule competes in — a stored rule or one about to be created.
export interface WorkflowGroupFields {
  subsystem: string;
  entity_type: string;
  action?: string | null;
  from_state?: string | null;
  to_state?: string | null;
}

export const clusterKeyFor = (workflow: WorkflowGroupFields): string => {
  const base = `${workflow.subsystem}|${workflow.entity_type}`;

  if (!isTransitionSubsystem(workflow.subsystem)) return base;
  const fromKey = `${base}|${workflow.action}|${workflow.from_state ?? ""}`;

  if (isAutoTransition(workflow.subsystem, workflow.action)) return fromKey;
  return `${fromKey}|${workflow.to_state ?? ""}`;
};

// Groups and clusters keep first-appearance order from the fetched page so
// the server sort still decides which groups lead.
export const groupWorkflows = (list: Workflow[]): WorkflowEntityGroup[] => {
  const groups: WorkflowEntityGroup[] = [];
  const groupIndex = new Map<string, WorkflowEntityGroup>();
  const clusterIndex = new Map<string, WorkflowCluster>();

  list.forEach((workflow) => {
    let group = groupIndex.get(workflow.entity_type);

    if (!group) {
      group = { entity_type: workflow.entity_type, clusters: [] };
      groupIndex.set(workflow.entity_type, group);
      groups.push(group);
    }

    const key = clusterKeyFor(workflow);
    let cluster = clusterIndex.get(key);

    if (!cluster) {
      const transition = isTransitionSubsystem(workflow.subsystem);
      const pairKeyed = transition && !isAutoTransition(workflow.subsystem, workflow.action);

      cluster = {
        key,
        subsystem: workflow.subsystem,
        entity_type: workflow.entity_type,
        action: transition ? workflow.action : undefined,
        from_state: transition ? workflow.from_state : undefined,
        to_state: pairKeyed ? workflow.to_state : undefined,
        rows: []
      };
      clusterIndex.set(key, cluster);
      group.clusters.push(cluster);
    }
    cluster.rows.push(workflow);
  });

  groups.forEach((group) => group.clusters.forEach((cluster) => cluster.rows.sort(byFiringOrder)));
  return groups;
};

// Trigger heading + fallback line only render for competing clusters (>=2).
export const flattenWorkflowGroups = (groups: WorkflowEntityGroup[]): WorkflowListRow[] => {
  const rows: WorkflowListRow[] = [];

  groups.forEach((group) => {
    rows.push({ kind: "group", id: `group-${group.entity_type}`, entity_type: group.entity_type });
    group.clusters.forEach((cluster) => {
      const competing = cluster.rows.length > 1;

      if (competing) rows.push({ kind: "cluster", id: `cluster-${cluster.key}`, cluster });
      cluster.rows.forEach((workflow) =>
        rows.push({ kind: "workflow", id: workflow.workflow_id, workflow })
      );
      if (competing) rows.push({ kind: "fallback", id: `fallback-${cluster.key}`, subsystem: cluster.subsystem });
    });
  });
  return rows;
};

const BLOCK_DOES_LABEL = "Stops the change";

// "What it does" chip — TRANSITION splits into blocks and auto-moves.
const clusterDoesLabel = (cluster: WorkflowCluster): string =>
  isBlockTransitionAction(cluster.action) ? BLOCK_DOES_LABEL : subsystemPlainLabel(cluster.subsystem);

// "Draft → Submitted" for a block pair, "From Draft" for an auto-move from-status group.
const statusPairLabel = (cluster: WorkflowCluster): string | null => {
  if (!cluster.from_state) return null;
  if (isAutoTransition(cluster.subsystem, cluster.action)) return `From ${workflowStatusLabel(cluster.from_state)}`;
  return cluster.to_state
    ? `${workflowStatusLabel(cluster.from_state)} → ${workflowStatusLabel(cluster.to_state)}`
    : null;
};

// "Moves it along · Draft → Submitted" — customer-language trigger heading.
export const clusterTriggerHeading = (cluster: WorkflowCluster): string => {
  const base = clusterDoesLabel(cluster);
  const pair = isTransitionSubsystem(cluster.subsystem) ? statusPairLabel(cluster) : null;

  return pair ? `${base} · ${pair}` : base;
};

// Always plural — the chip only renders when a cluster has >=2 rules.
export const competingCountLabel = (count: number): string => `${count} competing rules`;

export const CLUSTER_ORDER_HINT = "shown in firing order";

// Only first-match-wins clusters have an order worth editing: TRANSITION
// (blocks and auto-moves per pair) and DEFAULTS allocation splits.
export const isOrderableSubsystem = (subsystem?: string | null): boolean =>
  isTransitionSubsystem(subsystem) || isDefaultsSubsystem(subsystem);

// A new rule goes after the last rule in its group (active or not), so it lands last in the list.
export const nextPriorityInGroup = (candidate: WorkflowGroupFields, existing: Workflow[]): number => {
  const key = clusterKeyFor(candidate);
  const priorities = existing.filter((workflow) => clusterKeyFor(workflow) === key).map(firingPriority);

  if (priorities.length === 0) return ORDER_PRIORITY_STEP;
  return Math.max(...priorities) + ORDER_PRIORITY_STEP;
};

const GROUP_TITLES: Record<string, string> = {
  [SUBSYSTEM_NOTIFICATION]: "Notifications",
  [SUBSYSTEM_SCHEDULER]: "Scheduled checks",
  [SUBSYSTEM_DEFAULTS]: "Split the cost"
};

const AUTO_FALLBACK_COPY = "If none of these match, the record stays where it is.";

const GROUP_FALLBACK_COPY: Record<string, string> = {
  [SUBSYSTEM_NOTIFICATION]: "Every matching rule sends its email.",
  [SUBSYSTEM_SCHEDULER]: "Each rule runs on its own schedule."
};

const ORDERED_GROUP_HINT = "Checked in this order. The first match wins.";
const UNORDERED_GROUP_HINT = "All matching rules run.";
const ALL_OFF_GROUP_HINT = "Every rule here is switched off.";
const SINGLE_RULE_GROUP_HINT = "Only rule checked here.";

const orderableGroupHint = (activeCount: number): string => {
  if (activeCount === 0) return ALL_OFF_GROUP_HINT;
  if (activeCount === 1) return SINGLE_RULE_GROUP_HINT;
  return ORDERED_GROUP_HINT;
};

export interface WorkflowRuleGroup {
  key: string;
  title: string;
  does: string;
  orderable: boolean;
  hint: string;
  fallback: string;
  // Auto-move groups mix target statuses, so each row names its own.
  showTarget: boolean;
  // Orderable groups: active rules in firing order. Others: every rule.
  ordered: Workflow[];
  // Orderable groups only: inactive rules, which the engine never checks.
  off: Workflow[];
}

const groupTitle = (cluster: WorkflowCluster): string => {
  if (isTransitionSubsystem(cluster.subsystem)) return statusPairLabel(cluster) ?? clusterDoesLabel(cluster);
  return GROUP_TITLES[cluster.subsystem] ?? clusterDoesLabel(cluster);
};

const groupFallback = (cluster: WorkflowCluster): string => {
  if (isAutoTransition(cluster.subsystem, cluster.action)) return AUTO_FALLBACK_COPY;
  return GROUP_FALLBACK_COPY[cluster.subsystem] ?? subsystemFallbackCopy(cluster.subsystem);
};

const subsystemRank = (subsystem: string): number => {
  const index = SUBSYSTEM_OPTIONS.findIndex((option) => option.value === subsystem);

  return index === -1 ? SUBSYSTEM_OPTIONS.length : index;
};

// One object-type tab's rows as rule groups: first-match groups lead.
export const buildRuleGroups = (items: Workflow[]): WorkflowRuleGroup[] => {
  const clusters = groupWorkflows(items).flatMap((group) => group.clusters);

  return clusters
    .sort((a, b) => subsystemRank(a.subsystem) - subsystemRank(b.subsystem))
    .map((cluster) => {
      const orderable = isOrderableSubsystem(cluster.subsystem);
      const ordered = orderable ? cluster.rows.filter((workflow) => workflow.active) : cluster.rows;
      const hint = orderable ? orderableGroupHint(ordered.length) : UNORDERED_GROUP_HINT;

      return {
        key: cluster.key,
        title: groupTitle(cluster),
        does: clusterDoesLabel(cluster),
        orderable,
        hint,
        fallback: groupFallback(cluster),
        showTarget: isAutoTransition(cluster.subsystem, cluster.action),
        ordered,
        off: orderable ? cluster.rows.filter((workflow) => !workflow.active) : []
      };
    });
};

// A group shown in an unsaved order (rule ids in their new positions).
export const applyOrder = (group: WorkflowRuleGroup, order: string[]): WorkflowRuleGroup => {
  const byId = new Map(group.ordered.map((workflow) => [workflow.workflow_id, workflow]));
  const ordered = order.map((id) => byId.get(id)).filter((workflow): workflow is Workflow => Boolean(workflow));

  return { ...group, ordered };
};

export interface WorkflowOrderUpdate {
  workflow: Workflow;
  input: UpdateWorkflowInput;
}

// priority = position x 100 for every rule whose value changes. DEFAULTS
// writes scope.priority too (the engine reads it) — scope is replaced
// wholesale on update, so the stored scope is carried across.
export const buildOrderUpdates = (ordered: Workflow[]): WorkflowOrderUpdate[] => {
  const updates: WorkflowOrderUpdate[] = [];

  ordered.forEach((workflow, index) => {
    const priority = (index + 1) * ORDER_PRIORITY_STEP;
    const base = { workflow_id: workflow.workflow_id, version: workflow.version, priority };

    if (!isDefaultsSubsystem(workflow.subsystem)) {
      if (workflow.priority !== priority) updates.push({ workflow, input: base });
      return;
    }

    const scope = parseJson<Record<string, unknown>>(workflow.scope) ?? {};

    if (workflow.priority === priority && scope.priority === priority) return;
    updates.push({ workflow, input: { ...base, scope: JSON.stringify({ ...scope, priority }) } });
  });
  return updates;
};
